import apiClient from './api';

export interface Notification {
  id: number;
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  notification_type: {
    code: string;
    name: string;
    category: string;
  };
  related_object_type?: string;
  related_object_id?: number;
}

export interface NotificationStats {
  total: number;
  unread: number;
  read: number;
  by_category: Record<string, number>;
}

export interface NotificationPreferences {
  email_enabled: boolean;
  in_app_enabled: boolean;
  push_enabled: boolean;
}

export interface NotificationFilters {
  page?: number;
  per_page?: number;
  is_read?: boolean;
  notification_type?: string;
  search?: string;
  status?: 'all' | 'read' | 'unread';
  type?: 'all' | 'provider' | 'parent' | 'system';
}

// Request throttling utility
class RequestThrottler {
  private lastRequestTime = 0;
  private minDelay = 1000; // 1 second minimum delay between requests

  async throttle<T>(requestFn: () => Promise<T>): Promise<T> {
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    
    if (timeSinceLastRequest < this.minDelay) {
      const delay = this.minDelay - timeSinceLastRequest;
      await new Promise(resolve => setTimeout(resolve, delay));
    }
    
    this.lastRequestTime = Date.now();
    return requestFn();
  }
}

const throttler = new RequestThrottler();

class NotificationService {
  // Get notifications with optional filtering
  async getNotifications(filters: NotificationFilters = {}) {
    return throttler.throttle(async () => {
      const params = new URLSearchParams();
      
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.per_page) params.append('per_page', filters.per_page.toString());
      if (filters.is_read !== undefined) params.append('is_read', filters.is_read.toString());
      if (filters.notification_type) params.append('notification_type', filters.notification_type);
      if (filters.search) params.append('search', filters.search);
      if (filters.status) params.append('status', filters.status);
      if (filters.type) params.append('type', filters.type);

      const response = await apiClient.get(`/api/notifications/?${params.toString()}`);
      return response.data;
    });
  }

  // Get unread count
  async getUnreadCount() {
    return throttler.throttle(async () => {
      const response = await apiClient.get('/api/notifications/unread-count/');
      return response.data;
    });
  }

  // Mark notifications as read
  async markAsRead(notificationIds: number[]) {
    return throttler.throttle(async () => {
      const response = await apiClient.post('/api/notifications/mark-read/', {
        notification_ids: notificationIds
      });
      return response.data;
    });
  }

  // Mark all notifications as read
  async markAllAsRead() {
    return throttler.throttle(async () => {
      const response = await apiClient.post('/api/notifications/mark-all-read/');
      return response.data;
    });
  }

  // Get notification preferences
  async getPreferences() {
    return throttler.throttle(async () => {
      const response = await apiClient.get('/api/notifications/preferences/');
      return response.data;
    });
  }

  // Update notification preferences
  async updatePreferences(preferences: Partial<NotificationPreferences>) {
    return throttler.throttle(async () => {
      const response = await apiClient.post('/api/notifications/preferences/update/', preferences);
      return response.data;
    });
  }

  // Delete a notification
  async deleteNotification(notificationId: number) {
    return throttler.throttle(async () => {
      const response = await apiClient.delete(`/api/notifications/${notificationId}/delete/`);
      return response.data;
    });
  }

  // Get notification statistics
  async getNotificationStats() {
    return throttler.throttle(async () => {
      const response = await apiClient.get('/api/notifications/stats/');
      return response.data;
    });
  }

  // Get notification types
  async getNotificationTypes() {
    return throttler.throttle(async () => {
      const response = await apiClient.get('/api/notifications/types/');
      return response.data;
    });
  }
}

export const notificationService = new NotificationService();
export default notificationService; 